import React, { useEffect } from "react";
import { Route, Switch } from "react-router";
import { parse } from "query-string";

import {
  PageBody,
  Page,
  PageHeader,
  PageFooter,
} from "@sainsburys-tech/luna-site-layout";
import { reportError } from "@jsainsburyplc/id-ui-common";

import { urls, missionId } from "utils/urls";
import { isProd } from "utils/env";
import { sanitisePath } from "utils/sanitisePath";
import {
  MISSION_OAUTH2_SCOPE,
  clearOAuth2RedirectGuard,
  initiateOAuth2Flow,
} from "utils/initiateOAuth2";
import { CHALLENGE, DATA_CHALLENGE } from "constants/login";

//import useTrackPage from "hooks/useTrackPage";
import useCookies from "hooks/useCookies";

import LoginPage from "pages/LoginPage";
import MyIdConfirmationPage from "pages/MyIdConfirmationPage";
import MyIdEmailVerificationPage from "pages/MyIdEmailVerificationPage";
import MyIdSetPasswordPage from "pages/MyIdSetPasswordPage";
import OtpPage from "pages/OtpPage";
import InitiateRegistrationPage from "pages/InitiateRegistrationPage";
import { ProgressiveRegistration } from "pages/ProgressiveRegistration";
import RegistrationPage from "pages/RegistrationPage";
import RegistrationOtpPage from "pages/RegistrationOtpPage";
import FindOutMorePage from "pages/FindOutMorePage";
import ResetPasswordPage from "pages/ResetPasswordPage";
import ForgottenPasswordPage from "pages/ForgottenPasswordPage";
import ForgottenPasswordOtpPage from "pages/ForgottenPasswordOtpPage";
import InitiateAccountRecoveryPage from "pages/InitiateAccountRecoveryPage";
import AccountRecoveryOtpPage from "pages/AccountRecoveryOtpPage";
import WebAuthnRegistrationPage from "pages/WebAuthnRegistrationPage";
import KeepYourAccountSafePage from "pages/KeepYourAccountSafePage";

import CallbackPage from "pages/CallbackPage";
import Header from "components/Header";
import Startup from "components/Startup";
import FooterContent from "components/FooterContent";
import AccountConfirmation from "components/Login/Notifications/AccountConfirmation";
import EmailChangeVerification from "components/EmailChange/EmailChangeVerification";
import EmailChangeConfirmation from "components/EmailChange/EmailChangeConfirmation";
import { MultiMissionFooter } from "components/MultiMissionFooter";
import ScrollToTop from "components/ScrollToTop";

/**
 * Routes that are accessible without a login_challenge.
 * These are "pre-authentication" journeys: registration, password reset,
 * account recovery, email change confirmation, etc.  Initiating an OAuth2
 * flow on these pages would redirect the user AWAY to the login page before
 * they can complete the journey.
 *
 * Pattern list uses simple substring checks against window.location.pathname.
 * Each entry is a string that must appear at the END of the path (ignoring
 * any trailing query-string, which is not part of pathname).
 */
const CHALLENGE_FREE_PATH_SUFFIXES = [
  "/email-verification", // initiateRegistration
  "/register/mfa", // registrationOtp  (must come before /register/)
  "/forgotten-password",
  "/password/verify-otp",
  "/reset-password", // matches /reset-password and /reset-password/<token>
  "/account-recovery/verify-details",
  "/account-recovery/otp",
  "/find-out-more-about-myid",
  "/keep-your-account-safe",
  "/progressive-registration",
  "/email-change-verify", // matches /email-change-verify/<token>
  "/email-change-confirm", // matches /email-change-confirm/<token>
];

/**
 * Returns true when the current page is a journey that does not require a
 * login_challenge to start.  For /register/<token> we additionally check
 * whether the third path segment looks like a token (not "mfa").
 */
const isChallengeFreeRoute = () => {
  const path = window.location.pathname;
  if (CHALLENGE_FREE_PATH_SUFFIXES.some((suffix) => path.includes(suffix))) {
    return true;
  }
  // /register/<token>  — token is a non-empty segment that is not "mfa"
  const registerTokenMatch = path.match(/\/register\/([^/]+)$/);
  if (registerTokenMatch && registerTokenMatch[1] !== "mfa") {
    return true;
  }
  // /activate/<token>
  if (/\/activate\/[^/]+$/.test(path)) {
    return true;
  }
  return false;
};

const Routes = () => {
  const rootElem = document.querySelector("#root");
  const LOGIN_CHALLENGE_QS = parse(window.location.search)[CHALLENGE];
  const LOGIN_CHALLENGE = rootElem.getAttribute(DATA_CHALLENGE) || "";

  const { loginChallenge, setLoginChallenge } = useCookies();

  //useTrackPage();

  const loginChallengeNotPresent =
    !loginChallenge && !parse(window.location.search)[CHALLENGE];

  useEffect(() => {
    if (isProd() && loginChallengeNotPresent) {
      reportError(
        `Login Challenge not found - ${sanitisePath(window.location.pathname)}`
      );
    }
  }, [loginChallengeNotPresent]);

  useEffect(() => {
    const newLoginChallenge = LOGIN_CHALLENGE_QS || LOGIN_CHALLENGE;
    if (newLoginChallenge) {
      setLoginChallenge(newLoginChallenge, { path: "/" });
      // Mark that the challenge was received in THIS browser session (tab).
      // SilentLogin reads this flag to decide whether the cookie challenge is
      // fresh enough to use directly (came from GOL's OAuth2 flow in this session)
      // or whether it must initiate a new OAuth2 round-trip to get a fresh one.
      // sessionStorage is per-tab and is cleared when the tab is closed, so a
      // stale challenge from a previous session will never have this flag set.
      sessionStorage.setItem("login_challenge_set_in_session", "true");
    }
  }, [LOGIN_CHALLENGE, LOGIN_CHALLENGE_QS, setLoginChallenge]);

  // ── Clear the redirect-loop guard once a login_challenge arrives ────────────
  // The guard is set just before we redirect to /oauth2/auth.  As soon as
  // Hydra redirects back with a login_challenge in the URL we know the
  // round-trip succeeded, so we clear the guard.  This means that after any
  // subsequent error (e.g. CSRF failure) that drops the user back on the login
  // page without a challenge, the next render will re-initiate the flow rather
  // than silently doing nothing.
  useEffect(() => {
    if (LOGIN_CHALLENGE_QS || LOGIN_CHALLENGE) {
      clearOAuth2RedirectGuard();
    }
  }, [LOGIN_CHALLENGE_QS, LOGIN_CHALLENGE]);

  // ── Auto-initiate OAuth2 flow when no login_challenge is present ───────────
  // When the SPA is navigated to directly (without going through a Relying
  // Party that calls /oauth2/auth first), there is no login_challenge and the
  // user cannot log in.  Redirect to Hydra's /oauth2/auth endpoint so that
  // a challenge is created and the full authorization code flow begins.
  //
  // IMPORTANT: we deliberately use LOGIN_CHALLENGE_QS / LOGIN_CHALLENGE
  // (URL query param and data-attribute) as the source of truth here — NOT
  // the `loginChallenge` cookie.  The cookie persists across page loads and
  // a stale cookie from a previous flow would make `loginChallengeNotPresent`
  // false, silently preventing re-initiation on every second load.
  //
  // Skip in local development (process.env.NODE_ENV === "development") because
  // there is no nginx proxy / Hydra running locally.
  // Skip if missionId is unknown (e.g. the user landed on /login-ui/ directly
  // without a mission path segment).
  // Skip if we are on the /callback route — the code is being exchanged for
  // tokens and we must not start a new authorization flow.
  useEffect(() => {
    const hasFreshChallenge = !!(LOGIN_CHALLENGE_QS || LOGIN_CHALLENGE);
    const isCallbackPage = window.location.pathname.endsWith("/callback");
    // Skip if tokens for this mission are already stored in sessionStorage
    // (i.e. the OAuth2 flow already completed successfully this session).
    // Without this guard, after CallbackPage stores the tokens the login page
    // would immediately kick off another OAuth2 flow — Hydra would skip login
    // (active session), acceptLogin would fire again, and the loop would repeat.
    const hasStoredTokens = !!(
      missionId && sessionStorage.getItem(`${missionId}_tokens`)
    );
    // Skip on routes that are reachable without a login_challenge (registration,
    // forgotten-password, reset-password, account-recovery, etc.).  Redirecting
    // away to Hydra on these pages would prevent the user from completing the
    // journey — they need to start from the beginning of that flow, not from
    // the login page.
    const onChallengeFreeRoute = isChallengeFreeRoute();
    if (
      !hasFreshChallenge &&
      !isCallbackPage &&
      !onChallengeFreeRoute &&
      !hasStoredTokens &&
      missionId &&
      MISSION_OAUTH2_SCOPE[missionId] &&
      process.env.NODE_ENV !== "development"
    ) {
      initiateOAuth2Flow(missionId);
    }
    // missionId and initiateOAuth2Flow are module-level constants — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LOGIN_CHALLENGE_QS, LOGIN_CHALLENGE]);

  return (
    <Page>
      <ScrollToTop />
      <PageHeader>
        <Header />
      </PageHeader>
      <PageBody element="main">
        <Startup>
          <Switch>
            {/* OAuth2 authorization code callback — must appear before /:missionId routes */}
            <Route exact path="/:missionId/callback" component={CallbackPage} />
            <Route exact path={urls.login} component={LoginPage} />
            <Route
              exact
              path={urls.webAuthnAssertionFinish}
              component={LoginPage}
            />
            <Route
              exact
              path={urls.webAuthnRegister}
              component={WebAuthnRegistrationPage}
            />
            <Route
              exact
              path={urls.myIdConfirmationSamePassword}
              render={() => <MyIdConfirmationPage passwordConflict={false} />}
            />
            <Route
              exact
              path={urls.myIdEmailVerification}
              component={MyIdEmailVerificationPage}
            />
            <Route
              exact
              path={urls.myIdSetPassword}
              component={MyIdSetPasswordPage}
            />
            <Route
              exact
              path={urls.myIdConfirmationDiffPassword}
              render={() => <MyIdConfirmationPage passwordConflict={true} />}
            />
            <Route path={urls.otp} component={OtpPage} />
            <Route
              path={urls.activateAccount}
              component={AccountConfirmation}
            />
            <Route
              exact
              path={urls.forgottenPassword}
              component={ForgottenPasswordPage}
            />
            <Route
              path={urls.forgottenPasswordOtp}
              component={ForgottenPasswordOtpPage}
            />
            <Route
              path={urls.initiateAccountRecovery}
              component={InitiateAccountRecoveryPage}
            />
            <Route
              path={urls.accountRecoveryOtp}
              component={AccountRecoveryOtpPage}
            />
            <Route
              path={[urls.resetPassword, urls.resetPasswordNoToken]}
              render={(props) => <ResetPasswordPage {...props} />}
            />
            <Route
              path={urls.initiateRegistration}
              component={InitiateRegistrationPage}
            />
            <Route
              path={urls.registrationOtp}
              component={RegistrationOtpPage}
            />
            <Route path={urls.register} component={RegistrationPage} />
            <Route path={urls.findOutMore} component={FindOutMorePage} />
            <Route
              path={urls.keepYourAccountSafe}
              component={KeepYourAccountSafePage}
            />
            <Route
              path={urls.progressiveRegistration}
              component={ProgressiveRegistration}
            />
            <Route
              path={urls.emailChangeVerify}
              component={EmailChangeVerification}
            />
            <Route
              path={urls.emailChangeConfirm}
              component={EmailChangeConfirmation}
            />
          </Switch>
        </Startup>
      </PageBody>
      <PageFooter element="footer">
        <Switch>
          <Route
            path={[
              urls.forgottenPassword,
              urls.forgottenPasswordOtp,
              urls.findOutMore,
              urls.progressiveRegistration,
              urls.resetPassword,
              urls.initiateAccountRecovery,
              urls.accountRecoveryOtp,
              urls.register,
            ]}
            component={MultiMissionFooter}
          />
        </Switch>
        <div className="ln-c-footer ln-u-soft-ends-xl">
          <FooterContent />
        </div>
      </PageFooter>
    </Page>
  );
};

export default Routes;
